import React, { useState, useEffect, useCallback } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassBadge } from '../ui/GlassBadge';
import { apiClient } from '../../services/api';
import { Users, Clock, CheckCircle, DollarSign, RefreshCw, Settings, TrendingUp, Calendar, Award } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Types ──────────────────────────────────────────────────────────────────────

interface Affiliate {
    user_id: string;
    name: string;
    email: string;
    referral_code: string | null;
    earning_count: number;
    pending_cents: number;
    approved_cents: number;
    paid_cents: number;
    currency: string;
}

interface ApprovedEarning {
    earning_id: string;
    commission_cents: number;
    currency: string;
    status: string;
    created_at: string | null;
    stripe_invoice_id: string | null;
}

interface PromoRecord {
    promo_id: string;
    promo_rate: number;
    promo_rate_pct: number;
    base_rate: number;
    base_rate_pct: number;
    start_date: string;
    end_date: string;
    status: string;
    created_at: string | null;
    signups_during_window: number;
    earning_count: number;
    gross_revenue_cents: number;
    total_commission_cents: number;
    paid_commission_cents: number;
    pending_commission_cents: number;
    approved_commission_cents: number;
    base_rate_commission_cents: number;
    promo_uplift_cents: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtCents = (cents: number, currency: string): string =>
    `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;

const fmtCurrency = (cents: number, currency = 'aud') =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

const PROMO_STATUS_COLOURS: Record<string, string> = {
    active:    'var(--success, #10b981)',
    scheduled: 'var(--info, #3b82f6)',
    completed: 'var(--text-secondary, #6b7280)',
    cancelled: 'var(--danger, #ef4444)',
};

const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderBottom: '1px solid var(--stroke)',
    color: 'var(--text)',
    fontSize: '0.875rem',
    verticalAlign: 'middle',
};

const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '9px 14px',
    borderBottom: '1px solid var(--stroke)',
    color: 'var(--text-secondary)',
    fontWeight: 600,
    fontSize: '0.8rem',
    background: 'var(--panel-strong)',
    whiteSpace: 'nowrap',
};

// ── Component ──────────────────────────────────────────────────────────────────

const AffiliateAdminPanel: React.FC = () => {
    const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Approve-all state
    const [approvingUserId, setApprovingUserId] = useState<string | null>(null);

    // Mark-paid state
    const [payingUserId, setPayingUserId] = useState<string | null>(null);
    const [payoutRef, setPayoutRef] = useState('');
    const [payRefLoading, setPayRefLoading] = useState(false);
    const [payRefError, setPayRefError] = useState('');

    // Promo history state
    const [promoHistory, setPromoHistory] = useState<PromoRecord[]>([]);
    const [promoLoading, setPromoLoading] = useState(false);

    // Commission rate settings state
    const [rateSettings, setRateSettings] = useState<{
        base_rate: number;
        promo_rate: number | null;
        promo_start: string | null;
        promo_end: string | null;
    } | null>(null);
    const [rateLoading, setRateLoading] = useState(false);
    const [rateSaving, setRateSaving] = useState(false);
    const [rateError, setRateError] = useState('');
    const [rateSaved, setRateSaved] = useState(false);

    // Form state for editing commission rates
    const [baseRatePct, setBaseRatePct] = useState('');
    const [promoRatePct, setPromoRatePct] = useState('');
    const [promoStart, setPromoStart] = useState('');
    const [promoEnd, setPromoEnd] = useState('');

    // ── Load ────────────────────────────────────────────────────────────────────

    const loadAffiliates = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiClient.get('/admin/affiliates');
            setAffiliates(res.data.affiliates ?? []);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to load affiliates.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadRateSettings = async () => {
        setRateLoading(true);
        try {
            const res = await apiClient.get('/admin/affiliates/rate-settings');
            const d = res.data;
            setRateSettings(d);
            setBaseRatePct(String(Math.round(d.base_rate * 100)));
            setPromoRatePct(d.promo_rate ? String(Math.round(d.promo_rate * 100)) : '');
            setPromoStart(d.promo_start || '');
            setPromoEnd(d.promo_end || '');
        } catch {
            setRateError('Failed to load rate settings');
        } finally {
            setRateLoading(false);
        }
    };

    const loadPromoHistory = async () => {
        setPromoLoading(true);
        try {
            const res = await apiClient.get('/admin/affiliates/promo-history');
            setPromoHistory(res.data.promos ?? []);
        } catch {
            // non-fatal
        } finally {
            setPromoLoading(false);
        }
    };

    useEffect(() => {
        loadAffiliates();
        loadRateSettings();
        loadPromoHistory();
    }, [loadAffiliates]);

    // ── Approve all pending for one affiliate ───────────────────────────────────

    const handleApproveAll = async (affiliate: Affiliate) => {
        if (
            !window.confirm(
                `Approve all pending earnings for ${affiliate.name} (${fmtCents(affiliate.pending_cents, affiliate.currency)})?`
            )
        )
            return;

        setApprovingUserId(affiliate.user_id);
        try {
            // Set before_date to tomorrow so every pending earning is included
            const tomorrow = new Date(Date.now() + 86_400_000).toISOString();
            await apiClient.post('/admin/affiliates/bulk-approve', { before_date: tomorrow });
            await loadAffiliates();
        } catch (err: any) {
            alert(getApiError(err, 'Bulk approve failed.'));
        } finally {
            setApprovingUserId(null);
        }
    };

    // ── Mark paid flow ──────────────────────────────────────────────────────────

    const openPayRow = (userId: string) => {
        setPayingUserId(userId);
        setPayoutRef('');
        setPayRefError('');
    };

    const closePayRow = () => {
        setPayingUserId(null);
        setPayoutRef('');
        setPayRefError('');
    };

    const handleMarkPaid = async (affiliate: Affiliate) => {
        const ref = payoutRef.trim();
        if (!ref) {
            setPayRefError('Payout reference is required.');
            return;
        }

        setPayRefLoading(true);
        setPayRefError('');

        try {
            // Fetch individual approved earning IDs for this affiliate
            const earningsRes = await apiClient.get(
                `/admin/affiliates/${affiliate.user_id}/earnings`
            );
            const earnings: ApprovedEarning[] = earningsRes.data.earnings ?? [];

            if (earnings.length === 0) {
                setPayRefError('No approved earnings found for this affiliate.');
                setPayRefLoading(false);
                return;
            }

            // PATCH each earning to "paid" with the payout reference
            await Promise.all(
                earnings.map((e) =>
                    apiClient.patch(`/admin/affiliates/earnings/${e.earning_id}`, {
                        status: 'paid',
                        payout_reference: ref,
                    })
                )
            );

            closePayRow();
            await loadAffiliates();
        } catch (err: any) {
            setPayRefError(getApiError(err, 'Failed to mark as paid.'));
        } finally {
            setPayRefLoading(false);
        }
    };

    // ── Save commission rates ───────────────────────────────────────────────────

    const handleSaveRates = async () => {
        setRateSaving(true);
        setRateError('');
        setRateSaved(false);
        try {
            const payload: any = {
                base_rate: parseFloat(baseRatePct) / 100,
            };
            if (promoRatePct && promoStart && promoEnd) {
                payload.promo_rate = parseFloat(promoRatePct) / 100;
                payload.promo_start = promoStart;
                payload.promo_end = promoEnd;
            }
            await apiClient.put('/admin/affiliates/rate-settings', payload);
            setRateSaved(true);
            setTimeout(() => setRateSaved(false), 3000);
            loadRateSettings();
            loadPromoHistory();
        } catch (err: any) {
            setRateError(getApiError(err, 'Failed to save rate settings'));
        } finally {
            setRateSaving(false);
        }
    };

    // ── Derived stats ───────────────────────────────────────────────────────────

    const withPending = affiliates.filter((a) => a.pending_cents > 0);
    const withApproved = affiliates.filter((a) => a.approved_cents > 0);
    const withPaid = affiliates.filter((a) => a.paid_cents > 0);

    const totalPendingCents = affiliates.reduce((s, a) => s + a.pending_cents, 0);
    const totalPaidCents = affiliates.reduce((s, a) => s + a.paid_cents, 0);

    // Use the first affiliate's currency as the display currency (all assumed same)
    const displayCurrency = affiliates[0]?.currency ?? 'aud';

    // ── Render ──────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', padding: '2rem 0' }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Loading affiliates…
            </div>
        );
    }

    if (error) {
        return (
            <GlassCard padding="lg">
                <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>
            </GlassCard>
        );
    }

    // ── Effective rate display ──────────────────────────────────────────────────

    const today = new Date().toISOString().split('T')[0];
    const isPromoActive = rateSettings?.promo_rate != null
        && rateSettings.promo_start != null
        && rateSettings.promo_end != null
        && today >= rateSettings.promo_start
        && today <= rateSettings.promo_end;
    const effectiveRatePct = isPromoActive
        ? Math.round((rateSettings!.promo_rate!) * 100)
        : Math.round((rateSettings?.base_rate ?? 0.25) * 100);

    const inputStyle: React.CSSProperties = {
        background: 'var(--panel-strong)',
        border: '1px solid var(--stroke)',
        borderRadius: 6,
        padding: '0.4rem 0.6rem',
        color: 'var(--text)',
        fontSize: '0.875rem',
        width: '80px',
    };

    const dateInputStyle: React.CSSProperties = {
        background: 'var(--panel-strong)',
        border: '1px solid var(--stroke)',
        borderRadius: 6,
        padding: '0.4rem 0.6rem',
        color: 'var(--text)',
        fontSize: '0.875rem',
        width: '150px',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: 1000 }}>

            {/* ── Commission Rate Settings ─────────────────────────────────────── */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Settings size={17} style={{ color: 'var(--text-secondary)' }} />
                    Commission Rate Settings
                </h3>

                {rateLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        Loading…
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                        {/* Base Rate */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.875rem', color: 'var(--text)', fontWeight: 500, minWidth: 140 }}>
                                Base Rate (%)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={baseRatePct}
                                onChange={(e) => setBaseRatePct(e.target.value)}
                                style={inputStyle}
                            />
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                Applied when no promotional period is active
                            </span>
                        </div>

                        {/* Promotional Rate */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text)', fontWeight: 500 }}>
                                Promotional Period (optional)
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', minWidth: 140 }}>
                                    Promo Rate (%)
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={promoRatePct}
                                    onChange={(e) => setPromoRatePct(e.target.value)}
                                    placeholder="e.g. 40"
                                    style={inputStyle}
                                />
                                <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>From</label>
                                <input
                                    type="date"
                                    value={promoStart}
                                    onChange={(e) => setPromoStart(e.target.value)}
                                    style={dateInputStyle}
                                />
                                <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>To</label>
                                <input
                                    type="date"
                                    value={promoEnd}
                                    onChange={(e) => setPromoEnd(e.target.value)}
                                    style={dateInputStyle}
                                />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                Set a higher rate for a limited period. Leave blank to disable. Active promos override the base rate.
                            </span>
                        </div>

                        {/* Effective rate status */}
                        {rateSettings && (
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                                Current rate: <strong style={{ color: 'var(--text)' }}>{effectiveRatePct}%</strong>
                                {isPromoActive && (
                                    <span style={{ color: 'var(--warning)', marginLeft: '0.4rem' }}>
                                        (promotional — active until {rateSettings.promo_end})
                                    </span>
                                )}
                            </div>
                        )}

                        {rateError && (
                            <span style={{ fontSize: '0.85rem', color: 'var(--danger)' }}>{rateError}</span>
                        )}
                        {rateSaved && (
                            <span style={{ fontSize: '0.85rem', color: 'var(--success)', fontWeight: 500 }}>Saved!</span>
                        )}

                        <div>
                            <GlassButton
                                variant="primary"
                                size="sm"
                                onClick={handleSaveRates}
                                disabled={rateSaving}
                                loading={rateSaving}
                            >
                                Save Rate Settings
                            </GlassButton>
                        </div>

                    </div>
                )}
            </GlassCard>

            {/* ── Summary stats ───────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <GlassCard padding="md" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'rgba(96,165,250,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Users size={18} style={{ color: 'var(--info)' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>
                            {withPending.length}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                            Affiliates with pending balance
                        </div>
                    </div>
                </GlassCard>

                <GlassCard padding="md" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'rgba(245,200,76,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Clock size={18} style={{ color: 'var(--warning)' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>
                            {fmtCents(totalPendingCents, displayCurrency)}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                            Total pending
                        </div>
                    </div>
                </GlassCard>

                <GlassCard padding="md" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'rgba(54,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <DollarSign size={18} style={{ color: 'var(--success)' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>
                            {fmtCents(totalPaidCents, displayCurrency)}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                            Total paid to date
                        </div>
                    </div>
                </GlassCard>
            </div>

            {/* ── Pending Payouts ─────────────────────────────────────────────── */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                    Pending Payouts
                    <span style={{ marginLeft: 10 }}><GlassBadge variant="warning" size="sm">{withPending.length}</GlassBadge></span>
                </h3>

                {withPending.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        No affiliates with pending balances.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Affiliate', 'Referral Code', 'Earnings Count', 'Pending Amount', 'Actions'].map((h) => (
                                        <th key={h} style={thStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {withPending.map((a) => (
                                    <tr key={a.user_id}>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: 500 }}>{a.name}</div>
                                            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{a.email}</div>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                {a.referral_code ?? '—'}
                                            </span>
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{a.earning_count}</td>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                                            {fmtCents(a.pending_cents, a.currency)}
                                        </td>
                                        <td style={tdStyle}>
                                            <GlassButton
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleApproveAll(a)}
                                                loading={approvingUserId === a.user_id}
                                                disabled={!!approvingUserId}
                                                icon={<CheckCircle size={14} />}
                                            >
                                                Approve All
                                            </GlassButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>

            {/* ── Approved — ready to pay ─────────────────────────────────────── */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                    Approved — Ready to Pay
                    <span style={{ marginLeft: 10 }}><GlassBadge variant="info" size="sm">{withApproved.length}</GlassBadge></span>
                </h3>

                {withApproved.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        No affiliates with approved balances awaiting payment.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Affiliate', 'Amount to Pay', 'Actions'].map((h) => (
                                        <th key={h} style={thStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {withApproved.map((a) => (
                                    <React.Fragment key={a.user_id}>
                                        <tr>
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 500 }}>{a.name}</div>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{a.email}</div>
                                            </td>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                                                {fmtCents(a.approved_cents, a.currency)}
                                            </td>
                                            <td style={tdStyle}>
                                                {payingUserId === a.user_id ? (
                                                    <GlassButton
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={closePayRow}
                                                        disabled={payRefLoading}
                                                    >
                                                        Cancel
                                                    </GlassButton>
                                                ) : (
                                                    <GlassButton
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => openPayRow(a.user_id)}
                                                        icon={<DollarSign size={14} />}
                                                    >
                                                        Mark Paid
                                                    </GlassButton>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Inline pay form row */}
                                        {payingUserId === a.user_id && (
                                            <tr>
                                                <td colSpan={3} style={{ padding: '0 14px 14px', background: 'var(--panel-strong)', borderBottom: '1px solid var(--stroke)' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.75rem' }}>
                                                        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                                                            Payout Reference (e.g. bank transfer ID, PayPal batch)
                                                        </label>
                                                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <input
                                                                type="text"
                                                                value={payoutRef}
                                                                onChange={(e) => setPayoutRef(e.target.value)}
                                                                placeholder="e.g. BATCH-2026-03-12"
                                                                style={{
                                                                    flex: 1,
                                                                    minWidth: 200,
                                                                    padding: '8px 12px',
                                                                    borderRadius: 6,
                                                                    border: '1px solid var(--stroke)',
                                                                    background: 'var(--panel)',
                                                                    color: 'var(--text)',
                                                                    fontSize: '0.875rem',
                                                                    outline: 'none',
                                                                }}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') handleMarkPaid(a); }}
                                                                autoFocus
                                                            />
                                                            <GlassButton
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => handleMarkPaid(a)}
                                                                loading={payRefLoading}
                                                                disabled={!payoutRef.trim()}
                                                                icon={<CheckCircle size={14} />}
                                                            >
                                                                Confirm Payment
                                                            </GlassButton>
                                                        </div>
                                                        {payRefError && (
                                                            <span style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>
                                                                {payRefError}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>

            {/* ── Payment History ─────────────────────────────────────────────── */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                    Payment History
                    <span style={{ marginLeft: 10 }}><GlassBadge variant="success" size="sm">{withPaid.length}</GlassBadge></span>
                </h3>

                {withPaid.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        No payouts recorded yet.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Affiliate', 'Email', 'Total Paid'].map((h) => (
                                        <th key={h} style={thStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {withPaid.map((a) => (
                                    <tr key={a.user_id}>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: 500 }}>{a.name}</div>
                                        </td>
                                        <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                                            {a.email}
                                        </td>
                                        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--success)' }}>
                                            {fmtCents(a.paid_cents, a.currency)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>

            {/* ── Promo History ───────────────────────────────────────────────── */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={17} style={{ color: 'var(--text-secondary)' }} />
                    Promotional Period History
                </h3>

                {promoLoading ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading…</p>
                ) : promoHistory.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        No promotional periods have been configured yet. Use the Commission Rate Settings above to create one.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                                <tr>
                                    {['Period', 'Rate', 'Status', 'Signups', 'Gross Revenue', 'Commission Paid', 'vs Base Rate'].map(h => (
                                        <th key={h} style={{
                                            textAlign: 'left', padding: '8px 12px',
                                            borderBottom: '1px solid var(--stroke)',
                                            color: 'var(--text-secondary)', fontWeight: 600,
                                            whiteSpace: 'nowrap', background: 'var(--panel-strong)',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {promoHistory.map(p => (
                                    <tr key={p.promo_id} style={{ background: p.status === 'active' ? 'var(--primary, #2177FF)08' : undefined }}>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Calendar size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                                                {p.start_date} – {p.end_date}
                                            </div>
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{p.promo_rate_pct}%</span>
                                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginLeft: 4 }}>
                                                (base {p.base_rate_pct}%)
                                            </span>
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)' }}>
                                            <span style={{
                                                display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                                                fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
                                                background: (PROMO_STATUS_COLOURS[p.status] || 'var(--text-secondary)') + '22',
                                                color: PROMO_STATUS_COLOURS[p.status] || 'var(--text-secondary)',
                                            }}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text)', textAlign: 'center' }}>
                                            {p.signups_during_window}
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                                            {fmtCurrency(p.gross_revenue_cents)}
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', whiteSpace: 'nowrap' }}>
                                            <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                                                {fmtCurrency(p.total_commission_cents)}
                                            </div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: 2 }}>
                                                {fmtCurrency(p.paid_commission_cents)} paid · {fmtCurrency(p.pending_commission_cents + p.approved_commission_cents)} pending
                                            </div>
                                        </td>
                                        <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', whiteSpace: 'nowrap' }}>
                                            {p.promo_uplift_cents > 0 ? (
                                                <span style={{ color: 'var(--warning, #f59e0b)', fontWeight: 600 }}>
                                                    +{fmtCurrency(p.promo_uplift_cents)}
                                                </span>
                                            ) : p.promo_uplift_cents < 0 ? (
                                                <span style={{ color: 'var(--success, #10b981)', fontWeight: 600 }}>
                                                    {fmtCurrency(p.promo_uplift_cents)}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'var(--text-secondary)' }}>—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.75rem 0 0', lineHeight: 1.5 }}>
                            <strong>vs Base Rate</strong> — the additional commission cost of the promo over the base rate. Positive = promo cost more than base rate.
                        </p>
                    </div>
                )}
            </GlassCard>

        </div>
    );
};

export default AffiliateAdminPanel;
